/**
 * FeatureRequestDetailPage — view a submitted feature request.
 *
 * Read-only view by default; the YouTrack/Monday integration status
 * is editable from here (retry buttons). Authorized users can also
 * retry individual sides or both.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    ArrowLeft, AlertTriangle, CheckCircle2, RotateCw, ExternalLink,
    Loader2, History, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_CLS = {
    synchronized: "border-emerald-300/30 bg-emerald-300/5 text-emerald-200",
    failed:       "border-rose-300/30 bg-rose-300/5 text-rose-200",
    pending:      "border-white/15 bg-white/5 text-white/65",
};

export default function FeatureRequestDetailPage() {
    const { id } = useParams();
    const [fr, setFr] = useState(null);
    const [log, setLog] = useState([]);
    const [retrying, setRetrying] = useState(null);

    const load = async () => {
        try {
            const [{ data }, { data: hist }] = await Promise.all([
                api.get(`/feature-requests/${id}`),
                api.get(`/feature-requests/${id}/audit-log`).catch(() => ({ data: [] })),
            ]);
            setFr(data);
            setLog(hist || []);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
        }
    };
    useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const retry = async (which) => {
        setRetrying(which);
        try {
            const url = which === "all"
                ? `/feature-requests/${id}/integrations/retry-all`
                : `/feature-requests/${id}/integrations/${which}/retry`;
            await api.post(url);
            toast.success("Retried");
            await load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Retry failed");
        } finally {
            setRetrying(null);
        }
    };

    if (!fr) return <div className="text-sm text-white/55">Loading…</div>;

    const o = fr.originalRequest || {};
    const d = fr.definedRequest || {};
    const ev = fr.evaluation || {};
    const yt = fr.integrations?.youTrack || {};
    const md = fr.integrations?.monday || {};
    const isMandatory = fr.mandatoryOverride?.enabled;

    return (
        <div className="max-w-5xl space-y-6" data-testid="feature-request-detail">
            <div className="flex items-center justify-between gap-4">
                <Link to="/feature-requests" className="text-xs text-white/55 hover:text-white inline-flex items-center gap-1">
                    <ArrowLeft className="w-3.5 h-3.5" /> All feature requests
                </Link>
                <div className="text-[10px] font-mono text-white/30">{fr.featureRequestId}</div>
            </div>

            <header className="border border-white/10 rounded-2xl bg-white/[0.02] p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="text-[10px] px-2 py-0.5 rounded border border-white/20 bg-white/5 uppercase tracking-wider">
                                {fr.status}
                            </span>
                            <span className="text-[10px] px-2 py-0.5 rounded border border-violet-300/30 bg-violet-300/15 text-violet-100">
                                {ev.classification || "—"}
                            </span>
                            {isMandatory ? (
                                <span className="text-[10px] px-2 py-0.5 rounded border border-rose-300/30 bg-rose-500/20 text-rose-200 inline-flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" /> MANDATORY
                                </span>
                            ) : null}
                            {(d.tags || []).map((t) => (
                                <span key={t} className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-white/55">{t}</span>
                            ))}
                        </div>
                        <h1 className="font-display text-3xl">
                            {d.approvedTitle || o.title || "(untitled)"}
                        </h1>
                        <div className="text-sm text-white/55 mt-1">
                            Requested by <strong>{fr.source?.requesterName || "—"}</strong>
                            {fr.source?.department ? ` · ${fr.source.department}` : ""}
                            {fr.source?.clientName ? ` · ${fr.source.clientName}` : ""}
                        </div>
                    </div>
                    <div className="text-right shrink-0">
                        <div className="text-[10px] uppercase text-white/45 tracking-wide">Score</div>
                        <div className="text-5xl font-display">{ev.score ?? 0}</div>
                        <div className="text-xs text-white/40">/ 100</div>
                    </div>
                </div>
            </header>

            {/* ── Integrations ───────────────────────────────────────────── */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="fr-integrations">
                <IntegrationCard name="YouTrack" data={yt} retry={() => retry("youtrack")} testId="fr-youtrack"
                                 busy={retrying === "youtrack"} />
                <IntegrationCard name="Monday.com" data={md} retry={() => retry("monday")} testId="fr-monday"
                                 busy={retrying === "monday"} />
                <div className="md:col-span-2 flex justify-end">
                    <button onClick={() => retry("all")} disabled={!!retrying}
                            data-testid="fr-retry-all-btn"
                            className="px-3 py-1.5 text-xs border border-violet-300/30 text-violet-200 rounded-md hover:bg-violet-300/10 inline-flex items-center gap-1.5 disabled:opacity-50">
                        {retrying === "all" ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}
                        Retry both
                    </button>
                </div>
            </section>

            {/* ── Defined request ─────────────────────────────────────────── */}
            <Section title="Summary" testId="fr-section-summary">
                <p className="text-sm">{d.summary || "(none)"}</p>
            </Section>
            <Section title="Problem statement" testId="fr-section-problem">
                <p className="text-sm">{d.problemStatement || "(none)"}</p>
            </Section>
            <Section title="Desired outcome" testId="fr-section-desired">
                <p className="text-sm">{d.desiredOutcome || "(none)"}</p>
            </Section>
            <Section title="User stories" testId="fr-section-stories">
                <ul className="space-y-1 text-sm">
                    {(d.userStories || []).map((s, i) => (
                        <li key={i}>· As a <strong>{s.role}</strong>, I want to {s.action}, so that {s.benefit}.</li>
                    ))}
                    {(d.userStories || []).length === 0 ? <li className="text-white/40">none</li> : null}
                </ul>
            </Section>
            <Section title="Acceptance criteria" testId="fr-section-ac">
                <ul className="space-y-1 text-sm">
                    {(d.acceptanceCriteria || []).map((c, i) => (
                        <li key={i}>· Given {c.given}; when {c.when}; then {c.then}.</li>
                    ))}
                    {(d.acceptanceCriteria || []).length === 0 ? <li className="text-white/40">none</li> : null}
                </ul>
            </Section>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Section title="In scope" testId="fr-section-inscope" small>
                    <BulletList items={d.inScope} />
                </Section>
                <Section title="Out of scope" testId="fr-section-outofscope" small>
                    <BulletList items={d.outOfScope} />
                </Section>
                <Section title="Assumptions" testId="fr-section-assumptions" small>
                    <BulletList items={d.assumptions} />
                </Section>
                <Section title="Open questions" testId="fr-section-questions" small>
                    <BulletList items={d.openQuestions} />
                </Section>
            </div>

            {/* ── Components + Original ─────────────────────────────────── */}
            <Section title="Score components" testId="fr-section-components">
                <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    {Object.entries(ev.components || {}).map(([k, v]) => (
                        <div key={k} className="border border-white/10 rounded-md p-2.5 bg-white/[0.02]">
                            <dt className="text-white/45">{k}</dt>
                            <dd className="text-lg font-mono">{Number(v).toFixed(3)}</dd>
                        </div>
                    ))}
                </dl>
            </Section>
            <Section title="Original request (verbatim)" testId="fr-section-original">
                <div className="text-xs text-white/45 mb-1">Title</div>
                <div className="text-sm">{o.title || "—"}</div>
                <div className="text-xs text-white/45 mt-2 mb-1">Description</div>
                <pre className="text-sm whitespace-pre-wrap font-sans">{o.description}</pre>
            </Section>

            {/* ── Audit log ─────────────────────────────────────────────── */}
            <Section title="Audit history"
                     icon={<History className="w-3.5 h-3.5 text-white/50" />}
                     testId="fr-audit-log">
                <ul className="space-y-1 text-xs font-mono">
                    {log.map((row) => (
                        <li key={row.id} className="flex items-baseline gap-3">
                            <span className="text-white/30 w-44 shrink-0">{new Date(row.timestamp).toLocaleString()}</span>
                            <span className="text-violet-200 w-32 shrink-0">{row.action}</span>
                            <span className="text-white/45">{row.actor}</span>
                            <span className="text-white/30 truncate">{JSON.stringify(row.details)}</span>
                        </li>
                    ))}
                    {log.length === 0 ? <li className="text-white/40">no entries</li> : null}
                </ul>
            </Section>
        </div>
    );
}


function IntegrationCard({ name, data, retry, busy, testId }) {
    const status = data?.status || "pending";
    const url = data?.issueUrl || data?.itemUrl;
    const id = data?.issueId || data?.itemId;
    const cls = STATUS_CLS[status] || STATUS_CLS.pending;
    const Icon = status === "synchronized" ? CheckCircle2 : status === "failed" ? AlertTriangle : Sparkles;
    return (
        <div className={`border rounded-xl p-4 ${cls}`} data-testid={testId}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        <div className="font-medium">{name}</div>
                        {data?.mode === "mock" ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/55">MOCK</span>
                        ) : null}
                    </div>
                    <div className="text-[11px] mt-1">
                        Status: <strong>{status}</strong>
                        {id ? <> · ID: <code className="text-[11px]">{id}</code></> : null}
                    </div>
                    {data?.error ? (
                        <div className="text-[10px] mt-1 font-mono break-all">{data.error}</div>
                    ) : null}
                    {data?.lastSyncedAt ? (
                        <div className="text-[10px] mt-1 text-white/40">
                            last synced {new Date(data.lastSyncedAt).toLocaleString()}
                        </div>
                    ) : null}
                </div>
                <div className="shrink-0 flex flex-col gap-1.5">
                    {url ? (
                        <a href={url} target="_blank" rel="noopener noreferrer"
                           data-testid={`${testId}-open`}
                           className="text-[11px] inline-flex items-center gap-1 hover:underline">
                            Open <ExternalLink className="w-3 h-3" />
                        </a>
                    ) : null}
                    <button onClick={retry} disabled={busy}
                            data-testid={`${testId}-retry`}
                            className="text-[11px] inline-flex items-center gap-1 px-2 py-1 border border-current/30 rounded hover:bg-white/5 disabled:opacity-50">
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}
                        Retry
                    </button>
                </div>
            </div>
        </div>
    );
}

function Section({ title, icon, children, testId, small }) {
    return (
        <section className={`border border-white/10 rounded-xl bg-white/[0.02] ${small ? "p-3" : "p-5"}`}
                 data-testid={testId}>
            <div className="text-xs uppercase tracking-wider text-white/45 mb-2 inline-flex items-center gap-1.5">
                {icon} {title}
            </div>
            {children}
        </section>
    );
}

function BulletList({ items }) {
    const arr = items || [];
    if (arr.length === 0) return <div className="text-xs text-white/40">none</div>;
    return (
        <ul className="space-y-0.5 text-xs">
            {arr.map((it, i) => (
                <li key={i}>· {typeof it === "string" ? it : JSON.stringify(it)}</li>
            ))}
        </ul>
    );
}
